/**
 * Custom React hook for managing AI settings
 * with localStorage persistence
 *
 * This hook provides state management for supervisor agent configuration
 * and default agent settings, persisting across sessions.
 */

import { useCallback, useEffect, useState } from 'react'

/** Storage key for persisting AI settings in localStorage */
const AI_SETTINGS_KEY = 'taqat-ai-settings'

/**
 * Supervisor agent capability settings.
 * Controls which features are enabled for the AI supervisor agent.
 */
export interface SupervisorCapabilities {
  /** Enable web search capability for the agent */
  webSearch: boolean
  /** Enable sandbox (code execution) capability */
  sandbox: boolean
  /** Enable voice input/output mode */
  voiceMode: boolean
  /** Show tool execution details in the Computer Preview side panel */
  showComputerPreview: boolean
}

/** Supervisor agent configuration */
export interface SupervisorSettings {
  /** AI model to use (e.g., "openai/gpt-4o-mini") */
  model: string
  /** Model provider (e.g., "OPEN_ROUTER") */
  modelProvider: string
  /** System prompt / instructions for the supervisor */
  systemPrompt: string
  /** Supervisor capabilities */
  capabilities: SupervisorCapabilities
}

/** Default settings for new agents */
export interface DefaultAgentSettings {
  /** Default AI model for new agents */
  model: string
  /** Default model provider */
  modelProvider: string
  /** Default temperature (0-2) */
  temperature: number
  /** Default max tokens (1-32000) */
  maxTokens: number
  /** Default top P (0-1) */
  topP: number
  /** Enable file attachments by default */
  enableAttachments: boolean
  /** Show follow-up suggestions by default */
  showFollowUp: boolean
  /** Enable text-to-speech by default */
  textToSpeech: boolean
}

/** Complete AI settings structure */
export interface AISettings {
  /** Supervisor agent configuration */
  supervisor: SupervisorSettings
  /** Default settings for new agents */
  defaults: DefaultAgentSettings
}

/** Default supervisor settings */
const DEFAULT_SUPERVISOR: SupervisorSettings = {
  model: 'openai/gpt-4o-mini',
  modelProvider: 'OPEN_ROUTER',
  systemPrompt: '',
  capabilities: {
    webSearch: true,
    sandbox: false,
    voiceMode: false,
    showComputerPreview: true,
  },
}

/** Default agent settings */
const DEFAULT_AGENT_SETTINGS: DefaultAgentSettings = {
  model: 'openai/gpt-4o-mini',
  modelProvider: 'OPEN_ROUTER',
  temperature: 0.7,
  maxTokens: 1000,
  topP: 1,
  enableAttachments: false,
  showFollowUp: true,
  textToSpeech: false,
}

/** Default AI settings */
const DEFAULT_AI_SETTINGS: AISettings = {
  supervisor: DEFAULT_SUPERVISOR,
  defaults: DEFAULT_AGENT_SETTINGS,
}

/** Return type for useAISettings hook */
interface UseAISettingsReturn {
  /** Current AI settings */
  settings: AISettings
  /** Update supervisor settings */
  updateSupervisor: (updates: Partial<SupervisorSettings>) => void
  /** Update supervisor capabilities */
  updateSupervisorCapabilities: (updates: Partial<SupervisorCapabilities>) => void
  /** Update default agent settings */
  updateDefaults: (updates: Partial<DefaultAgentSettings>) => void
  /** Reset all settings to defaults */
  resetSettings: () => void
  /** Check if settings have been modified from defaults */
  isDirty: boolean
}

/**
 * Retrieves AI settings from localStorage
 * Falls back to default settings if none found or invalid
 */
function getStoredSettings(): AISettings {
  if (typeof window === 'undefined') {
    return DEFAULT_AI_SETTINGS
  }

  try {
    const stored = localStorage.getItem(AI_SETTINGS_KEY)
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<AISettings>
      return {
        supervisor: { ...DEFAULT_SUPERVISOR, ...parsed.supervisor },
        defaults: { ...DEFAULT_AGENT_SETTINGS, ...parsed.defaults },
      }
    }
  } catch {
    // Invalid JSON, return defaults
  }

  return DEFAULT_AI_SETTINGS
}

/**
 * Deep compare two objects for equality
 */
function deepEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

/**
 * Hook for managing AI settings with localStorage persistence
 *
 * @returns Object containing settings and update functions
 *
 * @example
 * ```tsx
 * function AISettingsPage() {
 *   const { settings, updateSupervisor, updateDefaults } = useAISettings();
 *
 *   return (
 *     <div>
 *       <Input
 *         value={settings.supervisor.systemPrompt}
 *         onChange={(e) => updateSupervisor({ systemPrompt: e.target.value })}
 *       />
 *     </div>
 *   );
 * }
 * ```
 */
export function useAISettings(): UseAISettingsReturn {
  const [settings, setSettings] = useState<AISettings>(getStoredSettings)

  /** Sync settings to localStorage whenever they change */
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(AI_SETTINGS_KEY, JSON.stringify(settings))
    }
  }, [settings])

  /** Update supervisor settings */
  const updateSupervisor = useCallback(
    (updates: Partial<SupervisorSettings>) => {
      setSettings((prev) => ({
        ...prev,
        supervisor: { ...prev.supervisor, ...updates },
      }))
    },
    []
  )

  /** Update supervisor capabilities */
  const updateSupervisorCapabilities = useCallback(
    (updates: Partial<SupervisorCapabilities>) => {
      setSettings((prev) => ({
        ...prev,
        supervisor: {
          ...prev.supervisor,
          capabilities: { ...prev.supervisor.capabilities, ...updates },
        },
      }))
    },
    []
  )

  /** Update default agent settings */
  const updateDefaults = useCallback(
    (updates: Partial<DefaultAgentSettings>) => {
      setSettings((prev) => ({
        ...prev,
        defaults: { ...prev.defaults, ...updates },
      }))
    },
    []
  )

  /** Reset all settings to defaults */
  const resetSettings = useCallback(() => {
    setSettings(DEFAULT_AI_SETTINGS)
  }, [])

  /** Check if settings differ from defaults */
  const isDirty = !deepEqual(settings, DEFAULT_AI_SETTINGS)

  return {
    settings,
    updateSupervisor,
    updateSupervisorCapabilities,
    updateDefaults,
    resetSettings,
    isDirty,
  }
}
